import React, { useContext, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import {
  AppContext,
  COMMON_CONSTANTS,
  EventEmitterWithIndex,
  getCurrentUserHook,
  getTranslatedText
} from '@nile/components';
import { getLabel } from '@nile/projects/src/Pebbles/ProjectListTableContainer';
import {
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION
} from '@nile/graphql';
import { GET_PROJECTS_LISTVIEW_META } from '@nile/projects/src/Graphql';
import { GET_ENTITY_PROPERTIES } from '@nile/graphql';
import ListViewColumns from '@nile/mangifera/src/Containers/SheetContainers/ListViewColumns';

const getColumns = ({ viewId, module }) => {
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const intl = useIntl();

  const getModuleMeta = GET_PROJECTS_LISTVIEW_META;
  const moduleMetaResponseKey = 'getProjectListViewMetaInfo';

  const [columnsData, setColumnsData] = useState({
    columns: [],
    selectedColumns: [],
    availableColumns: []
  });

  const [columnDataLoading, setColumnDataLoading] = useState(true);

  const allColumnsRef = React.useRef([]);

  const defaultColumnsRef = React.useRef([]);

  const { loading: entitiesLoading, data: allEntitiesMeta } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const { loading: viewMetaLoading, data: metaData } = useQuery(getModuleMeta, {
    onCompleted(data) {
      if (data?.[moduleMetaResponseKey]) {
        if (data?.[moduleMetaResponseKey]?.selectAttributes)
          allColumnsRef.current =
            data?.[moduleMetaResponseKey]?.selectAttributes;
        if (data?.[moduleMetaResponseKey]?.defaultSelectedAttributes)
          defaultColumnsRef.current =
            data?.[moduleMetaResponseKey]?.defaultSelectedAttributes;
      }
    }
  });

  const [getPersonalization] = useLazyQuery(GET_PERSONALIZATION_BY_NAME, {
    client: AccountClient,
    variables: {
      userId: user?.userId,
      category: `${module?.toLowerCase()}-columnPersonilsation`,
      name: viewId
    }
  });

  const handleColumns = async () => {
    setColumnDataLoading(true);
    const personalisationData = await getPersonalization();
    const columnsFromPersonalization =
      personalisationData?.data?.getPersonalizationByCategoryAndName?.value &&
      JSON.parse(
        personalisationData?.data?.getPersonalizationByCategoryAndName?.value
      )?.columns;

    const moduleEntitiesAttributes =
      allEntitiesMeta?.getEntitiesMeta?.find((item) => {
        return item.name?.toLowerCase() === module?.toLowerCase();
      })?.attributes || [];

    const selectedColumns =
      columnsFromPersonalization ?? defaultColumnsRef.current;

    const availableColumns = [];

    const constructedColumns = [];

    for (let i = 0; i < allColumnsRef.current.length; i++) {
      const col = allColumnsRef.current[i];

      let selectedAttr;

      if (col.split('.').length > 1) {
        const attr = col.split('.')[0];
        const attrCol = col.split('.')[1];
        const moduleSubEntites =
          allEntitiesMeta?.getEntitiesMeta?.find((item) => {
            return item.name?.toLowerCase() === attr?.toLowerCase();
          })?.attributes || [];

        selectedAttr = moduleSubEntites.find(
          (attr) => attr.name?.toLowerCase() === attrCol?.toLowerCase()
        );
      } else {
        selectedAttr = moduleEntitiesAttributes.find(
          (attr) => attr.name?.toLowerCase() === col?.toLowerCase()
        );
      }

      const columnData = {
        ...(selectedAttr || {}),
        id: col,
        name: col,
        label: getTranslatedText(
          intl,
          selectedAttr?.attributePlaceHolder?.label || getLabel(col)
        ),
        // dataType: getDataType({ name: col }),
        // filterType: getFilterType({ name: col }),
        disable: defaultColumnsRef.current?.includes(col),
        selected: !!selectedColumns.includes(col),
        defaultColumn: defaultColumnsRef.current.includes(col)
      };

      if (!selectedColumns.includes(col)) {
        availableColumns.push(col);
      }

      constructedColumns.push(columnData);
    }

    setColumnsData({
      columns: constructedColumns.sort(
        (col, nextCol) =>
          selectedColumns.indexOf(col.name) -
          selectedColumns.indexOf(nextCol.name)
      ),
      selectedColumns: selectedColumns,
      availableColumns: availableColumns
    });
    setColumnDataLoading(false);
  };

  useEffect(() => {
    EventEmitterWithIndex.subscribe('refreshProjectColumns', handleColumns);
    return () => {
      EventEmitterWithIndex.unsubscribe('refreshProjectColumns');
    };
  }, []);

  useEffect(() => {
    if (viewId && !viewMetaLoading) {
      handleColumns();
    }
  }, [viewId, viewMetaLoading]);

  return {
    loading: !!entitiesLoading || !!viewMetaLoading || !!columnDataLoading,
    ...columnsData
  };
};

const ProjectListColumns = (props) => {
  const { viewId, module } = props;

  const intl = useIntl();
  const user = getCurrentUserHook();

  const { columns, loading: columnLoading } = getColumns({
    module,
    viewId
  });

  const columnDataRef = useRef(null);
  columnDataRef.current = columns;

  const personalisedColumnsRef = useRef(null);

  const [tableColumnPersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient,
    update(cache, { data }) {
      let personalisedData = cache.readQuery({
        query: GET_PERSONALIZATION_BY_NAME,
        variables: {
          userId: user?.userId,
          category: `${module?.toLowerCase()}-columnPersonilsation`,
          name: viewId
        }
      });

      cache.writeQuery({
        query: GET_PERSONALIZATION_BY_NAME,
        variables: {
          userId: user?.userId,
          category: `${module?.toLowerCase()}-columnPersonilsation`,
          name: viewId
        },
        data: {
          getPersonalizationByCategoryAndName: {
            ...personalisedData?.getPersonalizationByCategoryAndName,
            value: JSON.stringify({ columns: personalisedColumnsRef.current })
          }
        }
      });
    },
    onCompleted({ savePersonalization }) {
      if (savePersonalization) {
        // refetchColumns();
        EventEmitterWithIndex.dispatch('refreshProjectColumns');
      }
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  const savePersonalizedColumns = (data) => {
    const columnData = [];
    for (let i = 0; i < data.length; i++) {
      columnData.push(data[i]?.name);
    }
    const personalizedColumn = {
      user: {
        firstName: user?.firstName,
        userId: user?.userId,
        email: user?.email,
        lastName: user?.lastName
      },
      category: `${module?.toLowerCase()}-columnPersonilsation`,
      name: viewId || '',
      value: {
        columns: columnData
      }
    };

    columnDataRef.current = columnData;

    personalisedColumnsRef.current = columnData;

    tableColumnPersonalization({
      variables: {
        ...personalizedColumn,
        value: JSON.stringify(personalizedColumn.value)
      }
    });
  };

  const updateParent = (data) => {
    savePersonalizedColumns(data);
  };

  return (
    <>
      <ListViewColumns
        text={getTranslatedText(intl, 'ticket.icon.column')}
        columns={columns?.length}
        sections={[
          {
            header: getTranslatedText(intl, 'ticket.columns.fields'),
            items: columnDataRef.current
          }
        ]}
        meta={{
          heading: getTranslatedText(intl, 'ticket.columns.header'),
          selectedTitle: getTranslatedText(intl, 'asset.columns')
        }}
        selectedItems={columns?.filter((col) => !!col?.selected)}
        updateParent={updateParent}
        buttonLoading={columnLoading}
      />
    </>
  );
};

export { getColumns };
export default ProjectListColumns;
